import { useEffect } from 'react';
import { useTranslation } from '../utils/useTranslation';
import { getLanguageFromPath, detectBrowserLanguage, languages } from '../utils/i18n';

export function SEOHead() {
    const { t } = useTranslation();
    const lang = getLanguageFromPath() || detectBrowserLanguage();

    useEffect(() => {
        // Atualiza o lang do HTML
        document.documentElement.lang = lang;

        const path = window.location.pathname.replace(/\/+$/, '');
        const segments = path.split('/').filter(Boolean);
        const shortLangs = Object.values(languages).map(l => l.short);
        const isLangPrefixed = segments.length > 0 && (shortLangs as string[]).includes(segments[0]);
        const segA = isLangPrefixed ? (segments[1] || '') : (segments[0] || '');
        const segB = isLangPrefixed ? (segments[2] || '') : (segments[1] || '');

        const isFeature = segA === 'features' && ['messages', 'wishlist', 'themes', 'ai'].includes(segB);
        const isInfo = ['how-it-works', 'faq'].includes(segA);
        const isStaticPage = ['about', 'terms', 'privacy', 'contact', 'blog'].includes(segA);

        const infoMap: Record<string, { title: string; desc: string }> = {
            'how-it-works': { title: t('info.how.title'), desc: t('info.how.subtitle') },
            'faq': { title: t('info.faq.title'), desc: t('info.faq.subtitle') }
        };

        const staticPageMap: Record<string, { title: string; desc: string }> = {
            'about': { title: t('about.title'), desc: t('about.welcome') },
            'terms': { title: t('terms.title'), desc: t('terms.sections.acceptance.content') },
            'privacy': { title: t('privacy.title'), desc: t('privacy.intro') },
            'contact': { title: t('contact.title'), desc: t('contact.subtitle') },
            'blog': { title: t('blog.title'), desc: t('blog.subtitle') }
        };

        const pageTitle = isFeature
            ? `${t(`features.pages.${segB}.hero.title`)} - ${t('home.title')}`
            : isInfo
                ? `${infoMap[segA].title} - ${t('home.title')}`
                : isStaticPage
                    ? `${staticPageMap[segA].title} - ${t('home.title')}`
                    : `${t('home.title')} - ${t('home.subtitle')}`;

        const pageDesc = isFeature
            ? t(`features.pages.${segB}.hero.subtitle`)
            : isInfo
                ? infoMap[segA].desc
                : isStaticPage
                    ? staticPageMap[segA].desc
                    : t('home.subtitle');

        document.title = pageTitle;

        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.setAttribute('name', 'description');
            document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', pageDesc);

        let robots = document.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
        }

        if (segA === 'how-it-works') {
            robots.setAttribute('content', 'noindex,nofollow');
        } else {
            robots.setAttribute('content', 'index,follow');
        }

        document.querySelectorAll('link[rel="alternate"]').forEach(link => link.remove());

        const baseUrl = 'https://mysterygift.online';

        Object.values(languages).forEach((langConfig) => {
            const link = document.createElement('link') as HTMLLinkElement;
            link.rel = 'alternate';
            link.hreflang = langConfig.short;

            if (langConfig.code === 'pt-BR') {
                link.href = baseUrl + '/';
            } else {
                link.href = `${baseUrl}/${langConfig.short}/`;
            }

            document.head.appendChild(link);
        });

        const canonicalPath = isFeature
            ? `/${isLangPrefixed ? languages[lang].short + '/' : ''}features/${segB}`
            : isInfo
                ? `/${isLangPrefixed ? languages[lang].short + '/' : ''}${segA}`
                : isStaticPage
                    ? `/${isLangPrefixed ? languages[lang].short + '/' : ''}${segA}`
                    : `/${isLangPrefixed ? languages[lang].short + '/' : ''}`;
        const canonicalHref = baseUrl + canonicalPath;
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
            canonical = document.createElement('link') as HTMLLinkElement;
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalHref;

        updateMetaTag('og:title', pageTitle);
        updateMetaTag('og:description', pageDesc);
        updateMetaTag('og:url', canonicalHref);
        updateMetaTag('og:locale', lang);
        updateMetaTag('og:type', 'website');
        updateMetaTag('og:site_name', t('home.title'));

        const ogImageMap: Record<string, string> = {
            'pt-BR': '/og/og-pt-BR.png',
            'en-US': '/og/og-en.png',
            'es-ES': '/og/og-es.png',
            'fr-FR': '/og/og-fr.png',
            'de-DE': '/og/og-de.png',
            'it-IT': '/og/og-it.png',
            'ja-JP': '/og/og-ja-JP.png',
            'zh-CN': '/og/og-zh-CN.png',
            'ko-KR': '/og/og-en.png'
        };
        const ogImageUrl = baseUrl + (ogImageMap[lang] || '/og/og-en.png');
        updateMetaTag('og:image', ogImageUrl);

        const twitterCard = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement | null || document.createElement('meta');
        twitterCard.setAttribute('name', 'twitter:card');
        twitterCard.setAttribute('content', 'summary_large_image');
        if (!twitterCard.parentNode) document.head.appendChild(twitterCard);

        let twitterImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
        if (!twitterImage) {
            twitterImage = document.createElement('meta');
            twitterImage.setAttribute('name', 'twitter:image');
            document.head.appendChild(twitterImage);
        }
        twitterImage.setAttribute('content', ogImageUrl);

        // Adiciona x-default
        const defaultLink = document.createElement('link') as HTMLLinkElement;
        defaultLink.rel = 'alternate';
        defaultLink.hreflang = 'x-default';
        defaultLink.href = baseUrl + '/';
        document.head.appendChild(defaultLink);

        addJsonLd('jsonld-website', {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: t('home.title'),
            url: canonicalHref,
            inLanguage: lang
        });

        addJsonLd('jsonld-organization', {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: t('home.title'),
            url: baseUrl,
            logo: `${baseUrl}/icons/icon-512x512.png`
        });

        addJsonLd('jsonld-webapp', {
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: t('home.title'),
            applicationCategory: 'Productivity',
            operatingSystem: 'Web',
            url: canonicalHref,
            inLanguage: lang
        });

        addJsonLd('jsonld-webpage', {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: pageTitle,
            description: pageDesc,
            url: canonicalHref,
            inLanguage: lang
        });

        const breadcrumbItems: any[] = [
            {
                '@type': 'ListItem',
                position: 1,
                name: t('nav.home'),
                item: baseUrl + (lang === 'pt-BR' ? '/' : `/${languages[lang].short}/`)
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: isFeature ? t(`features.pages.${segB}.hero.title`) : isInfo ? infoMap[segA].title : t('home.title'),
                item: canonicalHref
            }
        ];
        addJsonLd('jsonld-breadcrumbs', {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems
        });



    }, [lang, t]);

    return null; // Este componente não renderiza nada
}

function updateMetaTag(property: string, content: string) {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
}

function addJsonLd(id: string, obj: any) {
    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
    }
    script.text = JSON.stringify(obj);
}
